'use client';

import { useMemo } from 'react';

import { ThemeProvider } from 'next-themes';

import { I18nProvider } from '@kit/i18n/provider';
import { MonitoringProvider } from '@kit/monitoring/components';
import { AppEventsProvider } from '@kit/shared/events';
import { If } from '@kit/ui/if';
import { VersionUpdater } from '@kit/ui/version-updater';

import { AnalyticsProvider } from '~/components/analytics-provider';
import { AuthProvider } from '~/components/auth-provider';
import { CapacitorDeepLinkHandler } from '~/components/capacitor-deep-link-handler';
import { CapacitorPushNotificationsHandler } from '~/components/capacitor-push-notifications-handler';
import { CapacitorRestoredResultHandler } from '~/components/capacitor-restored-result-handler';
import { I18nRouteNamespaces } from '~/components/i18n-route-namespaces';
import appConfig from '~/config/app.config';
import featuresFlagConfig from '~/config/feature-flags.config';
import { i18nResolver } from '~/lib/i18n/i18n.resolver';
import { getI18nSettings } from '~/lib/i18n/i18n.settings';

import { NativeNavigationBoundary } from '~/(tenant)/_components/native/native-navigation';

import { ReactQueryProvider } from './react-query-provider';

type RootProvidersProps = React.PropsWithChildren<{
  lang?: string;
  theme?: string;
  nonce?: string;
  businessType?: string | null;
  /** Namespaces to preload for this request's area (default: all). */
  i18nNamespaces?: string[];
  isTenant?: boolean;
}>;

/**
 * The type cast from TranslationResource to Record<string, string> is required because
 * the I18nProvider Resolver type (from @kit/i18n/provider) uses a flat Record signature,
 * while our TranslationResource supports nested objects. i18next handles nested objects
 * correctly at runtime; this cast is safe.
 */
type I18nProviderResolver = (
  lang: string,
  ns: string,
) => Promise<Record<string, string>>;

export function RootProviders({
  lang,
  theme = appConfig.theme,
  nonce,
  businessType,
  i18nNamespaces,
  isTenant = false,
  children,
}: RootProvidersProps) {
  const namespacesKey = i18nNamespaces?.join(',');
  const i18nSettings = useMemo(
    () =>
      getI18nSettings(
        lang,
        namespacesKey ? namespacesKey.split(',') : undefined,
      ),
    [lang, namespacesKey],
  );

  return (
    <MonitoringProvider>
      <AppEventsProvider>
        <AnalyticsProvider>
          <ReactQueryProvider>
            <I18nProvider
              settings={i18nSettings}
              resolver={
                ((language: string, namespace: string) =>
                  i18nResolver(
                    language,
                    namespace,
                    businessType,
                  )) as I18nProviderResolver
              }
            >
              <I18nRouteNamespaces isTenant={isTenant}>
                <AuthProvider>
                  <ThemeProvider
                    attribute="class"
                    enableSystem
                    disableTransitionOnChange
                    defaultTheme={theme}
                    enableColorScheme={false}
                    nonce={nonce}
                  >
                    <CapacitorDeepLinkHandler />
                    <CapacitorPushNotificationsHandler />
                    <CapacitorRestoredResultHandler />
                    <NativeNavigationBoundary>{children}</NativeNavigationBoundary>
                  </ThemeProvider>
                </AuthProvider>
              </I18nRouteNamespaces>

              <If condition={featuresFlagConfig.enableVersionUpdater}>
                <VersionUpdater />
              </If>
            </I18nProvider>
          </ReactQueryProvider>
        </AnalyticsProvider>
      </AppEventsProvider>
    </MonitoringProvider>
  );
}
